import React, { useState, useCallback, useRef } from 'react';
import { FaClock, FaUser, FaGripVertical, FaChevronLeft, FaChevronRight, FaUserPlus, FaTruck, FaArrowLeft } from 'react-icons/fa';
import { Shift, AssignedEmployee, AssetAssignmentInfo } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import PlantafelService, { GanttShiftPosition } from '../services/PlantafelService';
import { toast } from 'react-toastify';
import { pdebugError } from '../utils/plantafelDebug';

interface PlantafelGanttBarProps {
  position: GanttShiftPosition;
  barHeight: number;
  workAreaId: number;
  workAreaColor?: string;
  onShiftClick: (shift: Shift, task?: CrmTask | null) => void;
  onMoveStart?: (shiftId: number, startX: number, startDayIndex: number, grabOffset: number) => void;
  onResizeStart?: (shiftId: number, startX: number, endDayIndex: number) => void;
  onAssignEmployee?: (shiftId: number, employeeId: number, employeeName?: string, fromShiftId?: number) => Promise<void>;
  onAssignAsset?: (shiftId: number, assetId: number) => Promise<void>;
  onUnscheduleShift?: (shiftId: number) => void;
  onCancelMove?: () => void;
  isDragging?: boolean;
  isResizing?: boolean;
  isHighlighted?: boolean;
}

export default function PlantafelGanttBar({
  position,
  barHeight,
  workAreaId,
  workAreaColor,
  onShiftClick,
  onMoveStart,
  onResizeStart,
  onAssignEmployee,
  onAssignAsset,
  onUnscheduleShift,
  onCancelMove,
  isDragging = false,
  isResizing = false,
  isHighlighted = false,
}: PlantafelGanttBarProps): React.JSX.Element {
  const { shift, task, left, width, clampedLeft, clampedRight } = position;
  const waColor = workAreaColor || shift.work_area__color || '#7c3aed';
  const statusColors = task ? PlantafelService.getStatusColor(task.status) : null;
  const dateRangeStr = PlantafelService.formatGanttDateRange(shift);

  const [isDropTarget, setIsDropTarget] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // FE-OPS-R22-3 Fix: Track if we initiated a custom (in-lane) drag to prevent native drag
  const isCustomDraggingRef = useRef(false);

  // Drag detection to suppress click after drag/resize
  const didDragRef = useRef(false);
  const lastDragAtRef = useRef<number>(0);
  const downPosRef = useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    downPosRef.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!downPosRef.current) return;
    const dx = Math.abs(e.clientX - downPosRef.current.x);
    const dy = Math.abs(e.clientY - downPosRef.current.y);
    if (dx + dy > 5) {
      didDragRef.current = true;
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (didDragRef.current) {
      lastDragAtRef.current = Date.now();
    }
    downPosRef.current = null;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types).map(t => t.toLowerCase());
    const hasEmployee = types.includes('employeeid');
    const hasAsset = types.includes('assetid');
    if (hasEmployee || hasAsset) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsDropTarget(true);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropTarget(false);

    // Try both camelCase and lowercase versions (HTML5 drag/drop is case-insensitive for types but getData might not be)
    const employeeId = e.dataTransfer.getData('employeeId') || e.dataTransfer.getData('employeeid');
    const employeeName = e.dataTransfer.getData('employeeName') || e.dataTransfer.getData('employeename');
    const assetId = e.dataTransfer.getData('assetId') || e.dataTransfer.getData('assetid');
    const assetName = e.dataTransfer.getData('assetName') || e.dataTransfer.getData('assetname');
    const fromShiftId = e.dataTransfer.getData('fromShiftId') || e.dataTransfer.getData('fromshiftid');

    if (employeeId && onAssignEmployee) {
      const employeeIdNum = parseInt(employeeId, 10);
      const fromShiftIdNum = fromShiftId ? parseInt(fromShiftId, 10) : undefined;

      // Don't reassign to the same shift
      if (fromShiftIdNum === shift.id) {
        return;
      }

      setAssigning(true);
      try {
        // Pass employeeName and fromShiftId - toast is handled in PlantafelBoard.handleAssignEmployee
        await onAssignEmployee(shift.id, employeeIdNum, employeeName || undefined, fromShiftIdNum);
      } catch (err) {
        // Error toast is handled in PlantafelBoard.handleAssignEmployee
        pdebugError('Failed to assign employee:', err);
      } finally {
        setAssigning(false);
      }
    } else if (assetId && onAssignAsset) {
      const assetIdNum = parseInt(assetId, 10);
      const fromShiftIdNum = fromShiftId ? parseInt(fromShiftId, 10) : null;

      // Don't reassign to the same shift
      if (fromShiftIdNum === shift.id) {
        return;
      }

      setAssigning(true);
      try {
        // If coming from another shift, unassign first
        if (fromShiftIdNum) {
          const PlantafelService = (await import('../services/PlantafelService')).default;
          await PlantafelService.unassignAssetFromShift(fromShiftIdNum, assetIdNum);
          toast.success(`${assetName || 'Asset'} umgeplant`);
        }
        // Toast for new assignment is handled in PlantafelBoard.handleAssignAsset
        await onAssignAsset(shift.id, assetIdNum);
      } catch (err: any) {
        // Error toast is handled in PlantafelBoard.handleAssignAsset
        pdebugError('Failed to assign asset:', err);
      } finally {
        setAssigning(false);
      }
    }
  }, [shift.id, onAssignEmployee, onAssignAsset]);

  const handleMouseDownMove = (e: React.MouseEvent) => {
    if (onMoveStart) {
      e.stopPropagation();

      // Drag threshold: require 5px movement before starting drag
      // This prevents accidental drags when user just wants to click
      const DRAG_THRESHOLD = 5;
      const startX = e.clientX;
      const startY = e.clientY;
      const barRect = e.currentTarget.getBoundingClientRect();
      const grabOffset = e.clientX - barRect.left;
      let dragStarted = false;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (dragStarted) return;

        const deltaX = Math.abs(moveEvent.clientX - startX);
        const deltaY = Math.abs(moveEvent.clientY - startY);

        // Only start drag if movement exceeds threshold
        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          dragStarted = true;
          isCustomDraggingRef.current = true;
          onMoveStart(shift.id, startX, position.startDayIndex, grabOffset);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        isCustomDraggingRef.current = false;
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseDownResize = (e: React.MouseEvent) => {
    if (onResizeStart) {
      e.preventDefault();
      e.stopPropagation();

      // Resize threshold: require 3px movement before starting resize
      const RESIZE_THRESHOLD = 3;
      const startX = e.clientX;
      let resizeStarted = false;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (resizeStarted) return;

        const deltaX = Math.abs(moveEvent.clientX - startX);
        if (deltaX > RESIZE_THRESHOLD) {
          resizeStarted = true;
          onResizeStart(shift.id, startX, position.endDayIndex);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Suppress click if we just finished a drag/resize
    if (didDragRef.current || Date.now() - lastDragAtRef.current < 250) {
      e.preventDefault();
      e.stopPropagation();
      didDragRef.current = false;
      return;
    }
    if (!isDragging && !isResizing) {
      onShiftClick(shift, task);
    }
  };

  // Handle native drag start (for cross-lane move)
  const handleDragStart = (e: React.DragEvent) => {
    // Cancel any in-progress custom drag
    if (onCancelMove) {
      onCancelMove();
    }
    isCustomDraggingRef.current = false;

    // Determine if this is a job shift
    const isJob = shift.is_job_shift === true ||
      (shift as any).is_job_shift === 1 ||
      (shift as any).is_job_shift === 'true' ||
      shift.crm_task != null;

    e.dataTransfer.setData('shiftId', shift.id.toString());
    e.dataTransfer.setData('workAreaId', workAreaId.toString());
    e.dataTransfer.setData('isJobShift', isJob ? 'true' : 'false');
    e.dataTransfer.setData('taskTitle', task?.title || shift.crm_task_title || 'Job');
    e.dataTransfer.effectAllowed = 'move';
  };

  // Removed padding to ensure visual alignment matches saved times
  const adjustedLeft = left;
  const adjustedWidth = width;

  // Generate unique animation name for this bar's WA color
  const glowAnimationName = `pulse-glow-${shift.id}`;

  // Check if this is a job shift that can be unscheduled
  const showBacklogHandle = (shift.is_job_shift === true || (shift as any).is_job_shift === 1 || shift.crm_task != null) && !clampedLeft;

  return (
    <>
      {isHighlighted && (
        <style>
          {`
            @keyframes ${glowAnimationName} {
              0% { box-shadow: 0 0 6px 2px ${waColor}60; }
              25% { box-shadow: 0 0 16px 6px ${waColor}aa; }
              50% { box-shadow: 0 0 6px 2px ${waColor}60; }
              75% { box-shadow: 0 0 16px 6px ${waColor}aa; }
              100% { box-shadow: 0 0 8px 3px ${waColor}80; }
            }
          `}
        </style>
      )}
    <div
      data-role="shift-bar"
      data-shift-id={shift.id}
      data-testid={`plantafel-shift-${shift.id}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{
        position: 'absolute',
        top: 4,
        left: adjustedLeft,
        width: adjustedWidth,
        height: barHeight,
        background: isHighlighted ? `${waColor}15` : isDropTarget ? '#d1fae5' : isDragging ? '#e0e7ff' : 'white',
        borderRadius: clampedLeft && clampedRight ? '0' : clampedLeft ? '0 6px 6px 0' : clampedRight ? '6px 0 0 6px' : '6px',
        cursor: isDragging ? 'grabbing' : 'pointer',
        boxShadow: isDropTarget
          ? '0 0 0 2px #10b981, 0 4px 12px rgba(16, 185, 129, 0.3)'
          : isDragging
          ? '0 8px 24px rgba(0,0,0,0.2)'
          : '0 1px 4px rgba(0,0,0,0.1)',
        animation: isHighlighted ? `${glowAnimationName} 1.5s ease-in-out forwards` : 'none',
        transform: isHighlighted ? 'scale(1.02)' : 'none',
        // Use only longhand border properties to avoid React warning
        borderTopWidth: isDropTarget ? '2px' : isDragging ? '1px' : '1px',
        borderTopStyle: 'solid',
        borderTopColor: isDropTarget ? '#10b981' : isDragging ? '#a5b4fc' : '#e0e3ef',
        borderRightWidth: isDropTarget ? '2px' : isDragging ? '1px' : '1px',
        borderRightStyle: 'solid',
        borderRightColor: isDropTarget ? '#10b981' : isDragging ? '#a5b4fc' : '#e0e3ef',
        borderBottomWidth: isDropTarget ? '2px' : isDragging ? '1px' : '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: isDropTarget ? '#10b981' : isDragging ? '#a5b4fc' : '#e0e3ef',
        borderLeftWidth: clampedLeft ? (isDropTarget ? '2px' : '1px') : '4px',
        borderLeftStyle: 'solid',
        borderLeftColor: clampedLeft
          ? (isDropTarget ? '#10b981' : '#e0e3ef')
          : (isDropTarget ? '#10b981' : waColor),
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        overflow: 'hidden',
        transition: isDragging || isResizing ? 'none' : 'all 0.15s',
        zIndex: isHighlighted ? 200 : isDragging || isDropTarget ? 100 : 1,
      }}
    >
      {/* Drop indicator overlay */}
      {isDropTarget && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(16, 185, 129, 0.1)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <FaUserPlus size={16} color="#10b981" />
        </div>
      )}
      {/* Draggable handle for moving job to backlog */}
      {showBacklogHandle && (
        <div
          draggable
          onDragStart={handleDragStart}
          title="In Backlog ziehen"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '18px',
            flexShrink: 0,
            background: `linear-gradient(90deg, ${waColor}30 0%, transparent 100%)`,
            borderRight: `1px dashed ${waColor}40`,
            cursor: 'grab',
          }}
        >
          <FaArrowLeft size={8} color={waColor} style={{ opacity: 0.6 }} />
        </div>
      )}
      {clampedLeft && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '18px',
            flexShrink: 0,
            background: `linear-gradient(90deg, ${waColor}40 0%, transparent 100%)`,
            borderRight: `2px solid ${waColor}`,
          }}
        >
          <FaChevronLeft size={10} color={waColor} />
        </div>
      )}

      <div
        onMouseDown={handleMouseDownMove}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '4px',
          padding: '4px 6px',
          cursor: 'grab',
          flex: 1,
          minWidth: 0,
        }}
      >
        {!clampedLeft && <FaGripVertical size={10} color="#999" style={{ flexShrink: 0, marginTop: '2px' }} />}

        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {/* Line 1: Time */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '2px',
            }}
          >
            <FaClock size={9} color="#888" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#021a4a', whiteSpace: 'nowrap' }}>
              {dateRangeStr}
            </span>
          </div>

          {/* Line 2: Customer - Job Name */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '3px',
              fontSize: '12px',
              overflow: 'hidden',
            }}
          >
            {(task?.partner_name || shift.partner_name) && (
              <>
                <span style={{ color: '#666', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {task?.partner_name || shift.partner_name}
                </span>
                <span style={{ color: '#999' }}>–</span>
              </>
            )}
            <span style={{ fontWeight: 700, color: '#021a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task?.title || shift.crm_task_title || (shift.is_job_shift ? 'Job' : 'Schicht')}
            </span>
          </div>

          {/* Line 3: Assigned Employees */}
          {shift.assigned_employees && shift.assigned_employees.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '2px',
                overflow: 'hidden',
              }}
            >
              <FaUser size={10} color="#1d4ed8" style={{ flexShrink: 0 }} />
              <span
                style={{
                  fontSize: '11px',
                  color: '#1d4ed8',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {shift.assigned_employees.slice(0, 3).map((e: AssignedEmployee) =>
                  e.first_name ? `${e.first_name} ${e.last_name?.[0] || ''}.` : 'MA'
                ).join(', ')}
                {shift.assigned_employees.length > 3 && ` +${shift.assigned_employees.length - 3}`}
              </span>
            </div>
          )}

          {/* Line 4: Assigned Assets */}
          {shift.asset_assignments && shift.asset_assignments.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflow: 'hidden',
              }}
            >
              <FaTruck size={10} color="#666" style={{ flexShrink: 0 }} />
              <span
                style={{
                  fontSize: '11px',
                  color: '#555',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {shift.asset_assignments.slice(0, 3).map((a: AssetAssignmentInfo) => a.asset_name || 'Asset').join(', ')}
                {shift.asset_assignments.length > 3 && ` +${shift.asset_assignments.length - 3}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {clampedRight ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '18px',
            flexShrink: 0,
            background: `linear-gradient(270deg, ${waColor}40 0%, transparent 100%)`,
            borderLeft: `2px solid ${waColor}`,
          }}
        >
          <FaChevronRight size={10} color={waColor} />
        </div>
      ) : (
        <div
          data-testid={`plantafel-shift-resize-${shift.id}`}
          onMouseDown={handleMouseDownResize}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            cursor: 'ew-resize',
            background: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: '2px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '20px',
              borderRadius: '2px',
              background: '#ccc',
            }}
          />
        </div>
      )}
    </div>
    </>
  );
}
